/**
 * AuthContext.js - Authentication context provider for TimeGenie
 *
 * Manages user authentication state and profile data throughout the app:
 * - User session management with Supabase Auth
 * - User profile CRUD operations
 * - Invite link processing for new user onboarding
 * - Sign out with comprehensive cleanup
 * - Auth state change listeners and initialization
 */

// Extend Window interface for global auth properties
/** @type {Window & { currentUser?: any; currentSession?: any; currentProfile?: any }} */
const typedWindow = /** @type {any} */ (window);

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { supabase } from '../config/supabase';
import useAuthListeners from '../hooks/useAuthListeners'; // Modular event listeners for auth context
import { getUserProfile as psGetUserProfile } from '../lib/services/profileService';
import logger from '../utils/logger'; // Centralized logger for consistent, level-based logging
import { subscribeToTable } from '../utils/supabaseUtils'; // Reuse existing realtime helpers
import { isEnabled } from '../utils/flags';
import { tasksKeys, goalsKeys, plansKeys, reviewsKeys } from '../lib/queryKeys';
import { cacheManager } from '../lib/cacheManager';
import { clearPersistedQueryCache } from '../lib/persistence/queryCacheDexie';
import { bootstrapSync } from '../lib/sync/bootstrapSync';
import {
  recordAppOpen,
  recordAppClose,
  recordSessionStart,
  recordSessionEnd,
} from '../utils/activityEvents';
import { initializeIOSPush } from '../lib/native/pushNotificationService';

// ------------------------
// Typedefs and safe defaults
// ------------------------
/**
 * @typedef {Object} SupabaseUser
 * @property {string} id
 * @property {string | null} [email]
 */

/**
 * @typedef {Object} UserProfile
 * @property {string} user_id
 * @property {string | null} [email]
 * @property {string | null} [name]
 * @property {string} [created_at]
 * @property {string} [updated_at]
 */

/**
 * @typedef {Object} AuthContextValue
 * @property {SupabaseUser | null} user
 * @property {UserProfile | null} profile
 * @property {boolean} loading
 * @property {(updates: Partial<UserProfile>) => Promise<UserProfile | null>} updateProfile
 * @property {() => Promise<{ error: null }>} signOut
 */

/** @type {AuthContextValue} */
const DEFAULT_AUTH_CONTEXT = Object.freeze({
  user: null,
  profile: null,
  loading: true,
  // no-op safe defaults; components should always be wrapped with provider
  async updateProfile() {
    return null;
  },
  async signOut() {
    return { error: null };
  },
});

// Create authentication context with a safe, typed default
/** @type {React.Context<AuthContextValue>} */
export const AuthContext = createContext(DEFAULT_AUTH_CONTEXT);

/**
 * AuthProvider - Main authentication provider component
 *
 * Provides authentication state and methods to all child components:
 * - user: Current authenticated user from Supabase Auth
 * - profile: User profile data from user_profiles table
 * - loading: Loading state during auth initialization
 * - updateProfile: Function to create/update user profile
 * - signOut: Function to sign out with full cleanup
 */
/**
 * @param {{ children: React.ReactNode }} props
 */
export const AuthProvider = ({ children }) => {
  // NOTE: We now pass concrete handlers to the listener hook (see definitions below).
  // This ensures we can hook a lightweight refresh on tab visibility and perform cleanup on unload.

  const [user, setUser] = useState(/** @type {SupabaseUser | null} */ (null)); // Current authenticated user
  const [profile, setProfile] = useState(/** @type {UserProfile | null} */ (null)); // User profile data
  const [loading, setLoading] = useState(/** @type {boolean} */ (true)); // Loading state
  const [prefetchCompleted, setPrefetchCompleted] = useState(
    /** @type {Set<string>} */ (new Set()),
  ); // Track completed prefetches by user ID
  const [prefetchFailed, setPrefetchFailed] = useState(/** @type {Set<string>} */ (new Set())); // Track failed prefetches by user ID (prevents retries)
  // Note: If toast is needed later, prefer Chakra's useToast in components
  const lastFetchedUserId = React.useRef(/** @type {string | null} */ (null)); // Track last userId for which profile was fetched
  const realtimeUnsubsRef = React.useRef(/** @type {Array<Function>} */ ([])); // Holds unsubscribe fns for Supabase Realtime subscriptions
  const bootstrapAbortRef = React.useRef(/** @type {AbortController | null} */ (null));
  // Track whether we've received the first auth event (INITIAL_SESSION/SIGNED_IN/SIGNED_OUT)
  const firstAuthEventRef = React.useRef(/** @type {boolean} */ (false));
  // Track an initialization grace timeout; cleared on first event or successful session load
  const initTimeoutRef = React.useRef(/** @type {number | null} */ (null));
  // Backup failsafe: ensure loading clears even if unexpected errors occur before grace timer starts
  const backupTimeoutRef = React.useRef(/** @type {number | null} */ (null));

  // TanStack Query client for targeted invalidations/patches
  const queryClient = useQueryClient();

  // Helper to read current chat session id for the given user, if any.
  // Matches the storage convention from `src/hooks/useChatManager.js`.
  /**
   * @param {string|undefined|null} userId
   * @returns {string|null}
   */
  const getStoredChatSessionId = (userId) => {
    try {
      if (typeof window === 'undefined' || !userId) return null;
      const key = `current_chat_session_id_${userId}`;
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const val = String(raw);
      const uuidLike =
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
      return uuidLike.test(val) ? val : null;
    } catch (_) {
      return null;
    }
  };

  useEffect(() => {
    return () => {
      if (bootstrapAbortRef.current) {
        try {
          bootstrapAbortRef.current.abort();
        } catch (_) {
          void 0;
        }
        bootstrapAbortRef.current = null;
      }
    };
  }, []);

  /**
   * fetchUserProfile - Retrieves user profile data from Supabase
   *
   * @param {string} userId - The user ID to fetch profile for
   * @returns {Object|null} - User profile object or null if not found
   *
   * Uses maybeSingle() to handle cases where no profile exists yet
   * Ignores PGRST116 (no rows) errors as they're expected for new users
   */
  /**
   * @param {string} userId
   * @returns {Promise<UserProfile | null>}
   */
  const fetchUserProfile = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('user_id, first_name, last_name, timezone, walkthrough_state, life_areas')
        .eq('user_id', userId)
        .maybeSingle(); // Use maybeSingle instead of single to handle no rows

      if (error && error.code !== 'PGRST116') {
        // Ignore 'no rows' error
        logger.error('Error fetching user profile:', error);
        return null;
      }
      return data || null;
    } catch (error) {
      logger.error('Exception in fetchUserProfile:', error);
      return null;
    }
  };

  /**
   * updateProfile - Creates or updates user profile in Supabase
   *
   * @param {Object} updates - Profile fields to update
   * @returns {Object|null} - Updated profile object or null on error
   *
   * Implements upsert logic:
   * 1. First attempts to update existing profile
   * 2. If no rows affected, creates new profile
   * 3. Automatically sets user_id, email, and timestamps
   */
  /**
   * @param {Partial<UserProfile>} updates
   * @returns {Promise<UserProfile | null>}
   */
  const updateProfile = async (updates) => {
    if (!user) {
      logger.debug('No user in updateProfile, skipping');
      return null;
    }

    try {
      // Only send fields that exist in the user_profiles schema. Avoid adding
      // columns like email/updated_at that may not exist in some deployments.
      const profileData = {
        ...updates,
        user_id: user.id,
      };

      // First try to update, if no rows affected, insert
      const { data: updatedData, error: updateError } = await supabase
        .from('user_profiles')
        .update(profileData)
        .eq('user_id', user.id)
        .select('user_id, first_name, last_name, timezone, walkthrough_state, life_areas')
        .single();

      if (updateError || !updatedData) {
        // If update failed, try insert
        const { data: insertedData, error: insertError } = await supabase
          .from('user_profiles')
          .insert([{ ...profileData }])
          .select('user_id, first_name, last_name, timezone, walkthrough_state, life_areas')
          .single();

        if (insertError) throw insertError;
        setProfile(insertedData);
        try {
          // Keep a global reference in sync so non-React modules can access latest profile
          window.currentProfile = insertedData;
        } catch (_) {
          void 0; // no-op
        }
        // Sync profileService cache so SettingsPage and other consumers see updated data
        cacheManager.set('user_profile', insertedData, user.id);
        return insertedData;
      }

      setProfile(updatedData);
      try {
        // Keep a global reference in sync so non-React modules can access latest profile
        window.currentProfile = updatedData;
      } catch (_) {
        void 0; // no-op
      }
      // Sync profileService cache so SettingsPage and other consumers see updated data
      cacheManager.set('user_profile', updatedData, user.id);
      return updatedData;
    } catch (error) {
      // Surface error to caller so UI can display the real cause (e.g., RLS or
      // unknown column). SettingsPage will catch and show a toast.
      logger.error('Error in updateProfile:', error);
      throw error;
    }
  };

  // Enhanced cleanup for tab close detection
  const handleBeforeUnload = useCallback(() => {
    sessionStorage.setItem('timegenie-tab-active', 'false');
    try {
      delete window.currentProfile;
    } catch (_) {
      void 0; // no-op
    }
    // Record app_close and session_end with duration
    try {
      const uid = /** @type {any} */ (user)?.id;
      if (uid) {
        const sid = getStoredChatSessionId(uid);
        void recordAppClose(uid, {
          sessionId: sid,
          metadata: { route: window.location?.pathname || '' },
        });
        // Also end the analytics session
        void recordSessionEnd(uid);
      }
    } catch (e) {
      logger.debug?.('AuthContext: recordAppClose (unload) best-effort failed', e);
    }
  }, [user]);

  const handleVisibilityChange = useCallback(() => {
    // Handle tab going to background (hidden)
    if (document.visibilityState === 'hidden') {
      logger.info('AuthContext: Tab became hidden - recording close event');
      try {
        const uid = /** @type {any} */ (user)?.id;
        if (uid) {
          const sid = getStoredChatSessionId(uid);
          void recordAppClose(uid, {
            sessionId: sid,
            metadata: { route: window.location?.pathname || '' },
          });
          // Also end the analytics session
          void recordSessionEnd(uid);
        }
      } catch (e) {
        logger.debug?.('AuthContext: recordAppClose (hidden) best-effort failed', e);
      }
      return;
    }

    // Handle tab becoming visible (foreground)
    if (document.visibilityState !== 'visible') return;
    logger.info('AuthContext: Tab became visible - reconciling data');
    // 1) Lightweight auth sanity check after long inactivity
    const lastAuth = sessionStorage.getItem('timegenie-last-auth');
    if (lastAuth) {
      try {
        const authData = JSON.parse(lastAuth);
        const timeSinceLastAuth = Date.now() - authData.timestamp;
        if (timeSinceLastAuth > 5 * 60 * 1000) {
          logger.info('AuthContext: Refreshing session due to inactivity >5m');
          supabase.auth.getSession().then(({ data: { session } }) => {
            if (!session && user) {
              logger.info('AuthContext: Session expired - clearing state');
              setUser(null);
              setProfile(null);
            }
          });
        }
      } catch (error) {
        logger.error('AuthContext: Error parsing stored auth data:', error);
      }
    }
    // 2) Under FF_SYNC_V2, realtime keeps caches up-to-date; no coarse refresh.
    // 3) Record session_start and app_open on foreground for authenticated users.
    try {
      const uid = /** @type {any} */ (user)?.id;
      if (uid) {
        // Begin a new analytics session (sets UUID and start time)
        void recordSessionStart(uid);
        const sid = getStoredChatSessionId(uid);
        void recordAppOpen(uid, {
          sessionId: sid,
          metadata: { route: window.location?.pathname || '' },
        });
      }
    } catch (e) {
      logger.debug?.('AuthContext: recordAppOpen (visible) best-effort failed', e);
    }
  }, [user]);

  // Register unload/visibility listeners using the existing hook
  useAuthListeners(handleBeforeUnload, handleVisibilityChange);

  // Also record foreground on window focus (deduped via TTL inside recordAppOpen).
  useEffect(() => {
    /** @returns {void} */
    const onFocus = () => {
      try {
        if (document.visibilityState !== 'visible') return;
        const uid = /** @type {any} */ (user)?.id;
        if (!uid) return;
        const sid = getStoredChatSessionId(uid);
        void recordAppOpen(uid, {
          sessionId: sid,
          metadata: { route: window.location?.pathname || '' },
        });
      } catch (e) {
        logger.debug?.('AuthContext: recordAppOpen (focus) best-effort failed', e);
      }
    };
    try {
      window.addEventListener('focus', onFocus);
    } catch (_) {
      // no-op
    }
    return () => {
      try {
        window.removeEventListener('focus', onFocus);
      } catch (_) {
        // no-op
      }
    };
  }, [user?.id]);

  // Initialize iOS native push notifications when user authenticates
  useEffect(() => {
    if (!user?.id) return;

    // Initialize iOS push in background (non-blocking)
    initializeIOSPush(user.id).catch((e) => {
      logger.debug?.('AuthContext: iOS push init failed (non-blocking):', e);
    });
  }, [user?.id]);

  // Initialize authentication state and set up listeners
  useEffect(() => {
    let isMounted = true;

    /**
     * setupRealtime - Subscribes to Supabase Realtime for chats and sessions using existing helpers.
     * Reuses `subscribeToTable` from `src/utils/supabaseUtils.js` to avoid new files.
     * Cleans up previous subscriptions before adding new ones.
     */
    /**
     * @param {string|null|undefined} uid
     */
    const setupRealtime = (uid) => {
      try {
        // Cleanup any old subscriptions first
        realtimeUnsubsRef.current.forEach((fn) => {
          try {
            fn && fn();
          } catch (_) {
            void 0; // no-op
          }
        });
        realtimeUnsubsRef.current = [];

        if (!uid) return;
        logger.info('🔔 AuthContext: Subscribing to Realtime for user:', uid);

        // Listen for changes in chat_messages for this user
        const unsubMessages = subscribeToTable(
          'chat_messages',
          { filter: `user_id=eq.${uid}` },
          /** @param {any} payload */
          (payload) => {
            if (!isMounted) return;
            const { eventType, new: newRow } = payload || {};
            logger.info('📩 Realtime(chat_messages):', eventType, newRow?.id || '');
            // Under FF_SYNC_V2, global realtime handles cache updates.
          },
        );

        // Listen for changes in chat_sessions (ordering/unread/etc.)
        const unsubSessions = subscribeToTable(
          'chat_sessions',
          { filter: `user_id=eq.${uid}` },
          /** @param {any} payload */
          (payload) => {
            if (!isMounted) return;
            const { eventType, new: newRow } = payload || {};
            logger.info('📁 Realtime(chat_sessions):', eventType, newRow?.id || '');
            // Under FF_SYNC_V2, global realtime handles cache updates.
          },
        );

        // --- Helper: patch list in persistent cache and TQ cache ---
        /**
         * Patch a list cache for a table by op.
         * @param {string} keyName - cache key name used by cacheManager (e.g., 'tasks')
         * @param {any[]} queryKey - TanStack Query key array
         * @param {'upsert'|'remove'} op
         * @param {any} row
         */
        const patchList = (keyName, queryKey, op, row) => {
          if (!row || !row.id) return;
          try {
            // Update persistent cache
            const prevArr = /** @type {any[]} */ (cacheManager.get(keyName, uid) || []);
            let nextArr = /** @type {any[]} */ (prevArr);
            if (op === 'remove') {
              nextArr = prevArr.filter((it) => String(it?.id) !== String(row.id));
            } else {
              let found = false;
              nextArr = prevArr.map((it) => {
                if (String(it?.id) === String(row.id)) {
                  found = true;
                  return { ...it, ...row };
                }
                return it;
              });
              if (!found) nextArr = [row, ...prevArr];
            }
            cacheManager.set(keyName, nextArr, uid);

            // Update TanStack Query cache
            queryClient.setQueryData(queryKey, (prev) => {
              const arr = Array.isArray(prev) ? prev.slice() : [];
              if (op === 'remove') {
                return arr.filter((it) => String(it?.id) !== String(row.id));
              }
              let found = false;
              const mapped = arr.map((it) => {
                if (String(it?.id) === String(row.id)) {
                  found = true;
                  return { ...it, ...row };
                }
                return it;
              });
              if (!found) mapped.unshift(row);
              return mapped;
            });
            // Invalidate prefix so any parameterized lists refresh as needed
            try {
              queryClient.invalidateQueries({ queryKey });
            } catch (_) {
              // best-effort
            }
          } catch (e) {
            logger.warn('Realtime patch failed (non-blocking):', e);
          }
        };

        // --- Tasks Realtime ---
        const unsubTasks = subscribeToTable(
          'tasks',
          { filter: `user_id=eq.${uid}` },
          /** @param {any} payload */
          (payload) => {
            if (!isMounted) return;
            const { eventType, new: newRow, old: oldRow } = payload || {};
            logger.info('🧱 Realtime(tasks):', eventType, newRow?.id || oldRow?.id || '');
            const key = tasksKeys.all(uid);
            const isSoftDeleted = Boolean(newRow?.deleted_at || newRow?.is_deleted);
            if (eventType === 'DELETE' || (eventType === 'UPDATE' && isSoftDeleted)) {
              patchList('tasks', key, 'remove', oldRow || newRow);
            } else if (eventType === 'INSERT' || eventType === 'UPDATE') {
              patchList('tasks', key, 'upsert', newRow);
            }
          },
        );

        // --- Goals Realtime ---
        const unsubGoals = subscribeToTable(
          'goals',
          { filter: `user_id=eq.${uid}` },
          /** @param {any} payload */
          (payload) => {
            if (!isMounted) return;
            const { eventType, new: newRow, old: oldRow } = payload || {};
            logger.info('🎯 Realtime(goals):', eventType, newRow?.id || oldRow?.id || '');
            const key = goalsKeys.all(uid);
            const isSoftDeleted = Boolean(newRow?.deleted_at || newRow?.is_deleted);
            if (eventType === 'DELETE' || (eventType === 'UPDATE' && isSoftDeleted)) {
              patchList('goals', key, 'remove', oldRow || newRow);
            } else if (eventType === 'INSERT' || eventType === 'UPDATE') {
              patchList('goals', key, 'upsert', newRow);
            }
          },
        );

        // --- Plans Realtime ---
        const unsubPlans = subscribeToTable(
          'plans',
          { filter: `user_id=eq.${uid}` },
          /** @param {any} payload */
          (payload) => {
            if (!isMounted) return;
            const { eventType, new: newRow, old: oldRow } = payload || {};
            logger.info('🗂️ Realtime(plans):', eventType, newRow?.id || oldRow?.id || '');
            const key = plansKeys.all(uid);
            const isSoftDeleted = Boolean(newRow?.deleted_at || newRow?.is_deleted);
            if (eventType === 'DELETE' || (eventType === 'UPDATE' && isSoftDeleted)) {
              patchList('plans', key, 'remove', oldRow || newRow);
            } else if (eventType === 'INSERT' || eventType === 'UPDATE') {
              patchList('plans', key, 'upsert', newRow);
            }
          },
        );

        // --- Reviews Realtime ---
        const unsubReviews = subscribeToTable(
          'reviews',
          { filter: `user_id=eq.${uid}` },
          /** @param {any} payload */
          (payload) => {
            if (!isMounted) return;
            const { eventType, new: newRow, old: oldRow } = payload || {};
            logger.info('📝 Realtime(reviews):', eventType, newRow?.id || oldRow?.id || '');
            const key = reviewsKeys.all(uid);
            const isSoftDeleted = Boolean(newRow?.deleted_at || newRow?.is_deleted);
            if (eventType === 'DELETE' || (eventType === 'UPDATE' && isSoftDeleted)) {
              patchList('reviews', key, 'remove', oldRow || newRow);
            } else if (eventType === 'INSERT' || eventType === 'UPDATE') {
              patchList('reviews', key, 'upsert', newRow);
            }
          },
        );

        realtimeUnsubsRef.current = [
          unsubMessages,
          unsubSessions,
          unsubTasks,
          unsubGoals,
          unsubPlans,
          unsubReviews,
        ];
      } catch (e) {
        logger.warn(
          'AuthContext: Error setting up Realtime (non-blocking):',
          /** @type {any} */ (e),
        );
        void 0; // no-op
      }
    };

    /**
     * initializeAuth - Simplified initialization focused on core authentication
     */
    const initializeAuth = async () => {
      try {
        logger.info('AuthContext: Starting simplified authentication initialization...');

        // Backup failsafe: never let the app hang indefinitely on loading
        if (backupTimeoutRef.current) {
          clearTimeout(backupTimeoutRef.current);
          backupTimeoutRef.current = null;
        }
        backupTimeoutRef.current = window.setTimeout(() => {
          logger.warn('⛑️ AuthContext: Backup failsafe triggered - forcing loading=false');
          setLoading(false);
        }, 10000);

        // If we're on explicit auth routes, don't gate the UI with the grace timer.
        // This ensures the login/reset pages are reachable immediately.
        const path = window.location.pathname || '';
        const lower = path.toLowerCase();
        // Consider multiple possible auth route variants to avoid blocking auth screens
        const isAuthRoute =
          lower.startsWith('/login') ||
          lower.startsWith('/signin') ||
          lower.startsWith('/auth') ||
          lower.startsWith('/reset-password') ||
          lower.startsWith('/password-reset') ||
          lower.startsWith('/invite-completion');
        if (isAuthRoute) {
          logger.info('AuthContext: Auth route detected - disabling loading immediately');
          setLoading(false);
          if (backupTimeoutRef.current) {
            clearTimeout(backupTimeoutRef.current);
            backupTimeoutRef.current = null;
          }
        }

        // If we are NOT on an auth route and the browser clearly has no Supabase tokens yet,
        // skip the long grace entirely so first-time visitors see /login immediately.
        if (!isAuthRoute) {
          let hasSupabaseTokens = false;
          try {
            const keys = [...Object.keys(localStorage || {}), ...Object.keys(sessionStorage || {})];
            hasSupabaseTokens = keys.some(
              (k) => k.includes('sb-') || k.toLowerCase().includes('supabase'),
            );
          } catch (_) {
            // no-op: assume no tokens if storage inspection fails
          }
          if (!hasSupabaseTokens) {
            logger.info(
              'AuthContext: No Supabase tokens found - disabling loading immediately on non-auth route',
            );
            setLoading(false);
            if (backupTimeoutRef.current) {
              clearTimeout(backupTimeoutRef.current);
              backupTimeoutRef.current = null;
            }
          }
        }

        // Check for auth tokens in URL hash (invite/signup links)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const type = hashParams.get('type');

        if (accessToken && refreshToken) {
          logger.info('AuthContext: Processing auth tokens from URL hash, type:', type);

          try {
            // Set the session using the tokens from the URL
            const {
              data: { session },
              error,
            } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });

            if (error) {
              logger.error('AuthContext: Error setting session from tokens:', error);
              throw error;
            }

            if (session?.user && isMounted) {
              logger.info('AuthContext: Session set successfully from invite link');
              setUser(session.user);

              // Clean up the URL hash to prevent reprocessing
              window.history.replaceState(null, '', window.location.pathname);

              // If this is a password recovery flow, send user to reset page immediately
              if (type === 'recovery') {
                logger.info('AuthContext: Recovery link detected - redirecting to /reset-password');
                window.location.href = '/reset-password';
                return;
              }

              // For invite links, fetch or create the user profile
              const profile = await fetchUserProfile(session.user.id);
              if (isMounted) {
                setProfile(profile);

                // If this is a new invited user without a complete profile, redirect to completion
                if (type === 'invite' && (!profile || !profile.name)) {
                  logger.info('AuthContext: New invited user - redirecting to completion page');
                  window.location.href = '/invite-completion';
                  return;
                }
              }
            }
          } catch (error) {
            logger.error('AuthContext: Failed to process invite tokens:', error);
            // Clear the hash and continue with normal session check
            window.history.replaceState(null, '', window.location.pathname);
          }
        } else {
          // Check for existing session on app initialization with a grace timer.
          // We avoid Promise.race so we don't prematurely mark the user as signed out
          // if the INITIAL_SESSION/SIGNED_IN event arrives slightly after getSession.
          logger.info('AuthContext: Checking for existing session with grace timer...');

          // Start a grace timeout that will flip loading=false only if no auth event arrives.
          // This prevents ProtectedRoute from redirecting before we process the first event.
          initTimeoutRef.current = window.setTimeout(() => {
            if (!isMounted) return;
            if (firstAuthEventRef.current) return; // Event arrived, ignore timer
            logger.warn('⏱️ AuthContext: Grace period elapsed with no auth event');
            logger.info('AuthContext: Proceeding without session after grace period');
            setUser(null);
            setProfile(null);
            setLoading(false);
            if (backupTimeoutRef.current) {
              clearTimeout(backupTimeoutRef.current);
              backupTimeoutRef.current = null;
            }
          }, 500); // shorten grace to 500ms to avoid long blank startup on first load

          try {
            const { data, error: sessionError } = await supabase.auth.getSession();
            const session = data?.session;

            if (sessionError) {
              logger.error('AuthContext: Session check error:', sessionError);
              if (isMounted && !firstAuthEventRef.current) {
                setUser(null);
                setProfile(null);
                // End loading only if no event has arrived to take over
                if (initTimeoutRef.current) {
                  clearTimeout(initTimeoutRef.current);
                  initTimeoutRef.current = null;
                }
                setLoading(false);
                if (backupTimeoutRef.current) {
                  clearTimeout(backupTimeoutRef.current);
                  backupTimeoutRef.current = null;
                }
              }
              return;
            }

            if (session?.user && isMounted) {
              const signedInUser = /** @type {SupabaseUser} */ (session.user);
              logger.info('✅ AuthContext: Found existing session for user:', signedInUser.id);
              logger.debug(
                '✅ AuthContext: Session expires at:',
                new Date((session.expires_at || 0) * 1000).toISOString(),
              );

              // Validate session is not expired
              const now = Date.now() / 1000;
              if ((session.expires_at || 0) > now) {
                logger.info('✅ AuthContext: Session is valid and not expired');
                setUser(signedInUser);

                // Fetch user profile for existing session
                const profile = await fetchUserProfile(signedInUser.id);
                if (isMounted) {
                  setProfile(profile);
                }

                // Test that the session actually works for database queries
                logger.debug('🧪 AuthContext: Testing session with database query...');
                try {
                  const { error: testError } = await supabase
                    .from('user_profiles')
                    .select('user_id')
                    .eq('user_id', signedInUser.id)
                    .limit(1)
                    .maybeSingle();

                  if (testError) {
                    logger.error('❌ AuthContext: Session test query failed:', testError);
                    logger.warn('AuthContext: Session exists but cannot be used for queries');
                  } else {
                    logger.info(
                      '✅ AuthContext: Session test query succeeded - session is fully functional',
                    );
                  }
                } catch (testErr) {
                  logger.error('❌ AuthContext: Session test failed:', testErr);
                  void 0; // no-op
                }

                // If no event has arrived yet, we can finish loading now
                if (!firstAuthEventRef.current && isMounted) {
                  if (initTimeoutRef.current) {
                    clearTimeout(initTimeoutRef.current);
                    initTimeoutRef.current = null;
                  }
                  setLoading(false);
                  if (backupTimeoutRef.current) {
                    clearTimeout(backupTimeoutRef.current);
                    backupTimeoutRef.current = null;
                  }
                }
              } else {
                logger.warn('⚠️ AuthContext: Session found but expired, clearing state');
                if (isMounted && !firstAuthEventRef.current) {
                  setUser(null);
                  setProfile(null);
                  if (initTimeoutRef.current) {
                    clearTimeout(initTimeoutRef.current);
                    initTimeoutRef.current = null;
                  }
                  setLoading(false);
                }
              }
            } else {
              logger.info('AuthContext: No existing session found');
              if (isMounted && !firstAuthEventRef.current) {
                setUser(null);
                setProfile(null);
                if (initTimeoutRef.current) {
                  clearTimeout(initTimeoutRef.current);
                  initTimeoutRef.current = null;
                }
                setLoading(false);
              }
            }
          } catch (error) {
            logger.error('AuthContext: Error checking session:', error);
            if (isMounted && !firstAuthEventRef.current) {
              setUser(null);
              setProfile(null);
              if (initTimeoutRef.current) {
                clearTimeout(initTimeoutRef.current);
                initTimeoutRef.current = null;
              }
              setLoading(false);
            }
          }
        }
      } catch (error) {
        logger.error('AuthContext: Error in initializeAuth:', error);
      }
    };

    /**
     * handleAuthStateChange - Handles authentication state changes from Supabase
     *
     * @param {string} event - The auth event type (SIGNED_IN, SIGNED_OUT, etc.)
     * @param {Object} session - The current session object
     */
    /**
     * @param {string} event
     * @param {{ user?: SupabaseUser, expires_at?: number } | null} session
     */
    const handleAuthStateChange = async (event, session) => {
      logger.info('🔍 AuthContext: Auth state changed:', {
        event,
        session: session ? 'present' : 'null',
      });
      // Mark that the first auth event has been received and clear any grace timer
      firstAuthEventRef.current = true;
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
        initTimeoutRef.current = null;
      }

      if (session?.user && isMounted) {
        const signedInUser = /** @type {SupabaseUser} */ (session.user);
        logger.info('✅ AuthContext: User authenticated:', signedInUser.id);
        // If we are switching users within the same browser profile, purge the
        // previous user's persistent caches and in-memory query caches to avoid
        // cross-account data residue. This enforces strict per-user partitioning.
        try {
          const prevUid =
            lastFetchedUserId.current || /** @type {any} */ (window.currentUser?.id) || null;
          if (prevUid && String(prevUid) !== String(signedInUser.id)) {
            logger.info('🧹 AuthContext: Detected user switch - purging previous user caches', {
              previous: prevUid,
              next: signedInUser.id,
            });
            // Clear all persistent caches for the previous user
            cacheManager.clear(prevUid);
            try {
              await clearPersistedQueryCache({ userId: prevUid });
            } catch (persistError) {
              logger.warn(
                'AuthContext: Failed to clear persisted queries for previous user',
                persistError,
              );
            }
            // Clear TanStack Query caches to remove any in-memory data tied to prev user
            try {
              queryClient.clear();
            } catch (_) {
              void 0; // non-blocking
            }
          }
        } catch (e) {
          logger.warn('AuthContext: Error during user-switch purge (non-blocking):', e);
        }
        setUser(signedInUser);
        // Expose the authenticated user/session globally for low-level services that
        // may initialize before React context consumers (e.g., service modules).
        // This avoids early calls hanging on getSession and enables immediate data fetches.
        try {
          typedWindow.currentUser = signedInUser;
          typedWindow.currentSession = /** @type {any} */ (session);
          // Store user ID for feature flag beta user detection
          localStorage.setItem('timegenie_user_id', signedInUser.id);
        } catch (_) {
          void 0; // no-op
        }

        // Kick off dependency-aware baseline bootstrap in the background.
        try {
          if (bootstrapAbortRef.current) {
            bootstrapAbortRef.current.abort();
          }
          const controller = new AbortController();
          bootstrapAbortRef.current = controller;
          bootstrapSync(signedInUser.id, { signal: controller.signal })
            .catch((e) => {
              if (e instanceof DOMException && e.name === 'AbortError') return;
              logger.warn('AuthContext: bootstrap sync failed (non-blocking):', e);
            })
            .finally(() => {
              if (bootstrapAbortRef.current === controller) {
                bootstrapAbortRef.current = null;
              }
            });
        } catch (e) {
          logger.warn('AuthContext: Failed to schedule bootstrap sync (non-blocking):', e);
        }

        // End loading immediately on SIGNED_IN to avoid backup timer racing
        if (isMounted) {
          setLoading(false);
          if (backupTimeoutRef.current) {
            clearTimeout(backupTimeoutRef.current);
            backupTimeoutRef.current = null;
          }
        }

        // Start legacy realtime subscriptions only if FF_SYNC_V2 is disabled.
        // When FF_SYNC_V2 is enabled, we rely on src/lib/realtime.js as the
        // single source of truth to avoid duplicate events.
        if (!isEnabled('FF_SYNC_V2')) {
          setupRealtime(signedInUser.id);
        }

        // Fetch profile and prefetch in background (non-blocking)
        (async () => {
          try {
            const profile = await fetchUserProfile(signedInUser.id);
            if (!profile) {
              try {
                delete window.currentProfile;
              } catch (_) {
                void 0;
              }
            }
            if (profile && isMounted) {
              setProfile(profile);
              try {
                window.currentProfile = /** @type {any} */ (profile);
              } catch (_) {
                void 0; // no-op
              }
              // Warm profileService cache for timezone consumers
              try {
                await psGetUserProfile(signedInUser.id, { forceRefresh: true });
              } catch (_) {
                void 0; // non-blocking
              }
            }

            // Baseline bootstrap runs separately; realtime and queries keep caches fresh afterwards.
            // Track the current user id after successful initialization for future switch detection
            lastFetchedUserId.current = signedInUser.id;
          } catch (e) {
            logger.warn('AuthContext: Background profile/prefetch errored (non-blocking):', e);
          }
        })();
      } else if (isMounted) {
        logger.info('🚪 AuthContext: User signed out or no session');
        if (bootstrapAbortRef.current) {
          try {
            bootstrapAbortRef.current.abort();
          } catch (_) {
            void 0;
          }
          bootstrapAbortRef.current = null;
        }
        setUser(null);
        setProfile(null);
        // Clear global references to prevent stale access
        try {
          delete typedWindow.currentUser;
          delete typedWindow.currentSession;
          delete typedWindow.currentProfile;
        } catch (_) {
          void 0; // no-op
        }

        // Clear prefetch tracking
        setPrefetchCompleted(new Set());
        setPrefetchFailed(new Set());
        lastFetchedUserId.current = null;

        // Stop realtime subscriptions when signed out
        try {
          realtimeUnsubsRef.current.forEach((fn) => {
            try {
              fn && fn();
            } catch (_) {
              void 0; // no-op
            }
          });
          realtimeUnsubsRef.current = [];
        } catch (_) {
          void 0; // no-op
        }
        // Purge persistent and in-memory caches for the previously authenticated user
        try {
          // Attempt to infer the last user ID from a saved ref first
          const prevUid =
            lastFetchedUserId.current || /** @type {any} */ (window.currentUser?.id) || null;
          if (prevUid) {
            logger.info('🧹 AuthContext: Purging caches on SIGNED_OUT for user:', prevUid);
            cacheManager.clear(prevUid);
            try {
              await clearPersistedQueryCache({ userId: prevUid });
            } catch (persistError) {
              logger.warn(
                'AuthContext: Failed clearing persisted queries on SIGNED_OUT',
                persistError,
              );
            }
          }
          try {
            queryClient.clear();
          } catch (_) {
            void 0; // best-effort
          }
        } catch (e) {
          logger.warn('AuthContext: Error during sign-out purge (non-blocking):', e);
        }
      }
    };

    // Set up auth state change listener
    logger.info('🔍 AuthContext: Setting up auth state change listener...');
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(handleAuthStateChange);

    // Initialize auth state
    initializeAuth();

    // Modular event listeners for beforeunload and visibilitychange are now handled by useAuthListeners hook.
    // No need to register or clean up these listeners here.

    // Cleanup function
    return () => {
      logger.info('🧹 AuthContext: Cleaning up listeners and subscriptions');
      isMounted = false;
      subscription?.unsubscribe();
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
        initTimeoutRef.current = null;
      }
      if (backupTimeoutRef.current) {
        clearTimeout(backupTimeoutRef.current);
        backupTimeoutRef.current = null;
      }
      // Clean up realtime subscriptions explicitly on unmount
      try {
        realtimeUnsubsRef.current.forEach((fn) => {
          try {
            fn && fn();
          } catch (_) {
            void 0; // no-op
          }
        });
        realtimeUnsubsRef.current = [];
      } catch (_) {
        void 0; // no-op
      }
      // No event listener cleanup needed here
    };
  }, []); // Empty dependency array to run only once on mount

  // Sign out function with comprehensive cleanup
  const signOut = async () => {
    try {
      logger.info('AuthContext: Starting signOut process');

      // Clear user state immediately
      setUser(null);
      setProfile(null);

      // Clear all storage comprehensively
      try {
        // Clear localStorage
        // Scan and remove any Supabase-related keys that might exist
        // This catches project-specific keys and any legacy token formats
        Object.keys(localStorage).forEach((key) => {
          if (key.includes('supabase') || key.includes('sb-')) {
            localStorage.removeItem(key);
          }
        });
        // Clear feature flag user ID
        localStorage.removeItem('timegenie_user_id');

        Object.keys(sessionStorage).forEach((key) => {
          if (key.includes('supabase') || key.includes('sb-')) {
            sessionStorage.removeItem(key);
          }
        });

        logger.debug('AuthContext: Cleared all storage');
      } catch (storageError) {
        logger.warn('AuthContext: Error clearing storage:', storageError);
      }

      // Purge per-user persistent cache and in-memory queries
      try {
        const prevUid =
          lastFetchedUserId.current || /** @type {any} */ (window.currentUser?.id) || null;
        if (prevUid) {
          logger.info('🧹 AuthContext: Purging cacheManager for user during signOut:', prevUid);
          cacheManager.clear(prevUid);
          try {
            await clearPersistedQueryCache({ userId: prevUid });
          } catch (persistError) {
            logger.warn(
              'AuthContext: Failed clearing persisted queries during signOut',
              persistError,
            );
          }
        }
        try {
          queryClient.clear();
        } catch (_) {
          void 0; // non-blocking
        }
      } catch (e) {
        logger.warn('AuthContext: Error purging caches during signOut (non-blocking):', e);
      }

      // ...
      // Sign out from Supabase using local scope only
      // Local scope clears client-side tokens without server round-trip
      // This prevents failures due to network issues or expired tokens
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      logger.info('AuthContext: Supabase signOut result:', { error });

      if (error) {
        logger.error('AuthContext: Supabase signOut error:', error);
        // Don't throw error - we've already cleared local state and storage
      }

      // Force page reload and redirect to ensure completely clean state
      // Timeout ensures any pending state updates complete first
      logger.info('AuthContext: SignOut completed, forcing page reload for clean state');
      setTimeout(() => {
        window.location.href = '/login';
      }, 100);

      return { error: null };
    } catch (error) {
      logger.error('AuthContext: Error in signOut function:', error);
      // Even if there's an error, ensure state is cleared
      setUser(null);
      setProfile(null);

      // Force redirect as fallback
      setTimeout(() => {
        window.location.href = '/login';
      }, 100);

      return { error: null }; // Return success since we cleared local state
    }
  };

  const value = {
    user,
    profile,
    loading,
    updateProfile,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * useAuth - Hook to access authentication context
 *
 * @returns {Object} - Authentication context with user, profile, loading, updateProfile, signOut
 * @throws {Error} - If used outside of AuthProvider
 *
 * Must be used within components wrapped by AuthProvider
 * Provides access to current user state and auth methods
 */
export const useAuth = () => {
  /** @type {AuthContextValue} */
  const context = useContext(AuthContext);
  // If ever used outside provider and default changed to undefined
  if (context === undefined || context === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
